// Derive the denormalized structured columns (calendar_*, email_*,
// terminal_*, call_duration_seconds) and the side-table rows
// (calendar_attendees, email_participants, call_participants,
// message_mentions) from a validated per-kind payload. Pure — no I/O.
// Extracted from `message-builders.ts` to keep the builder entry under the
// LOC ceiling.

import type { MessageKind } from "./message-payload";
import type {
  BuiltMessage,
  MessageMentionRow,
  StructuredColumns,
} from "./message-builder-types";
import { epochMsOr, numberOr, stringOr } from "./message-coerce";

export function emptyStructuredColumns(): StructuredColumns {
  return {
    calendar_start_at: null,
    calendar_end_at: null,
    calendar_title: null,
    calendar_description: null,
    email_subject: null,
    email_body: null,
    email_from_email: null,
    terminal_command: null,
    terminal_output: null,
    terminal_exit_code: null,
    call_duration_seconds: null,
  };
}

export function extractStructuredColumns(
  kind: MessageKind,
  payload: Record<string, unknown> | null,
): StructuredColumns {
  const cols = emptyStructuredColumns();
  if (payload == null) return cols;
  switch (kind) {
    case "calendar":
      cols.calendar_title = stringOr(payload.title ?? null);
      cols.calendar_description = stringOr(payload.description ?? null);
      cols.calendar_start_at = epochMsOr(payload.start_at ?? payload.start);
      cols.calendar_end_at = epochMsOr(payload.end_at ?? payload.end);
      return cols;
    case "email":
      cols.email_subject = stringOr(payload.subject ?? null);
      cols.email_body = stringOr(payload.body ?? payload.html_body ?? null);
      cols.email_from_email = stringOr(
        payload.from_email ?? payload.from ?? null,
      );
      return cols;
    case "terminal_output":
    case "run":
      cols.terminal_command = stringOr(
        payload.command ?? payload.title ?? null,
      );
      cols.terminal_output = stringOr(
        payload.output ?? payload.content ?? null,
      );
      cols.terminal_exit_code = numberOr(payload.exit_code ?? null);
      return cols;
    case "terminal_snapshot":
    case "terminal_live":
    case "terminal_recording":
    case "command_history":
      cols.terminal_command = stringOr(
        payload.command ?? payload.title ?? null,
      );
      cols.terminal_output = stringOr(
        payload.output ?? payload.content ?? payload.plain ?? null,
      );
      return cols;
    case "call_ended":
      cols.call_duration_seconds = numberOr(
        payload.duration_seconds ?? payload.duration ?? null,
      );
      return cols;
    default:
      return cols;
  }
}

export function extractSideTableRows(
  messageId: string,
  kind: MessageKind,
  payload: Record<string, unknown> | null,
  mentions: string[] | null,
): BuiltMessage["sideTableRows"] {
  const rows: BuiltMessage["sideTableRows"] = {
    calendar_attendees: [],
    email_participants: [],
    call_participants: [],
    message_mentions: extractMessageMentionRows(messageId, mentions),
  };
  if (payload == null) return rows;

  if (kind === "calendar") {
    const attendees = Array.isArray(payload.attendees)
      ? (payload.attendees as unknown[])
      : [];
    const seen = new Set<string>();
    for (const raw of attendees) {
      if (!raw || typeof raw !== "object") continue;
      const obj = raw as Record<string, unknown>;
      const user_id = stringOr(obj.user_id ?? null);
      const email = stringOr(obj.email ?? null);
      if (!user_id && !email) continue;
      const dedupeKey = `${user_id ?? ""}|${email ?? ""}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      rows.calendar_attendees.push({
        id: `${messageId}:${dedupeKey}`,
        message_id: messageId,
        user_id,
        email,
        rsvp_status: stringOr(obj.rsvp_status ?? null),
      });
    }
  }

  if (kind === "email") {
    const participants = Array.isArray(payload.participants)
      ? (payload.participants as unknown[])
      : [];
    const fromEmail = stringOr(payload.from_email ?? payload.from ?? null);
    const seen = new Set<string>();
    if (fromEmail) {
      seen.add(`from|${fromEmail}`);
      rows.email_participants.push({
        message_id: messageId,
        email: fromEmail,
        role: "from",
      });
    }
    for (const raw of participants) {
      if (!raw || typeof raw !== "object") continue;
      const obj = raw as Record<string, unknown>;
      const email = stringOr(obj.email ?? null);
      const role = stringOr(obj.role ?? "to") ?? "to";
      if (!email) continue;
      const key = `${role}|${email}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.email_participants.push({
        message_id: messageId,
        email,
        role,
      });
    }
  }

  if (
    kind === "call_started_video" ||
    kind === "call_started_audio" ||
    kind === "call_ended"
  ) {
    const participants = Array.isArray(payload.participants)
      ? (payload.participants as unknown[])
      : [];
    const seen = new Set<string>();
    for (const raw of participants) {
      if (!raw || typeof raw !== "object") continue;
      const obj = raw as Record<string, unknown>;
      const user_id = stringOr(obj.user_id ?? null);
      if (!user_id || seen.has(user_id)) continue;
      seen.add(user_id);
      rows.call_participants.push({
        message_id: messageId,
        user_id,
        joined_at: numberOr(obj.joined_at ?? null),
        left_at: numberOr(obj.left_at ?? null),
      });
    }
  }

  return rows;
}

/**
 * Filter + dedupe mentions for the junction. Skip null, skip empty
 * strings, dedupe duplicates. `syncMessageMentionsJunction` (UPDATE
 * path) inlines the same filter rules.
 */
export function extractMessageMentionRows(
  messageId: string,
  mentions: string[] | null,
): MessageMentionRow[] {
  if (!mentions || mentions.length === 0) return [];
  const seen = new Set<string>();
  const rows: MessageMentionRow[] = [];
  for (const raw of mentions) {
    if (typeof raw !== "string") continue;
    if (raw === "") continue;
    if (seen.has(raw)) continue;
    seen.add(raw);
    rows.push({ message_id: messageId, user_id: raw });
  }
  return rows;
}
